<template>
  <SkeletonText :loading="loading">
    <div class="matter-staff">
      <div class="matter-staff__unassigned" v-if="!assignedStaff">
        {{$t('client.matters.matter_page.primary_staff')}}:
        <button class="matter-staff__change--btn"
                @click="changeStaffClick">
          {{$t('client.matters.matter_page.unassigned')}}
        </button>
      </div>
      <div v-if="assignedStaff"
         :class="{ 'matter-staff--photo': assignedStaff.photo_path && !allAdditionalInitials }">

        <div class="staff primary-staff"
             :class="{ 'primary-staff--single': !availableStaff.length }">

          <div class="staff-title"
               :class="{ 'staff-title--initials': !assignedStaff.photo_path, 'staff-title--single': !additionalStaff.length, 'staff-title--gape': !isAdditionalStaff }">
            {{$t('client.matters.matter_page.primary_staff')}}:
          </div>

          <div @click="changeStaffClick">
            <v-tooltip top
                       content-class="avatar-tooltip">
              <template v-slot:activator="{ on, attrs }">
                <div class="staff-avatar-wrapper"
                     v-bind="attrs"
                     v-on="on">
                  <AvatarImage class="staff-avatar"
                               size="x-sm"
                               :image-path="assignedStaff.photo_path"
                               :name="assignedStaff.display_name"
                               :color-id="assignedStaff.color_id"/>
                </div>
              </template>
              <span>
              {{assignedStaff.display_name}}
            </span>
            </v-tooltip>
          </div>
          <button class="matter-staff__change--btn"
               :class="{ 'matter-staff__change--initials': !assignedStaff.photo_path }"
               @click="changeStaffClick"
               v-if="isCollaborator">
            {{$t('client.matters.matter_page.change_staff')}}
          </button>

        </div>

        <div class="staff additional-staff"
             v-if="showAdditionalStaff">

          <div class="staff-title"
               :class="{ 'staff-title--initials': allAdditionalInitials, 'staff-title--photos': allAdditionalPhotos }">
            {{$t('client.matters.matter_page.additional_staff')}}:
          </div>

          <div class="avatars" v-if="additionalStaff.length" @click="openAddStaffDialog">
            <!--up to 3 icon of staff-->
            <v-tooltip top
                       content-class="avatar-tooltip"
                       v-for="staff in additionalStaff.slice(0,3)"
                       :key="staff.display_name">
              <template v-slot:activator="{ on, attrs }">
                <div class="staff-avatar-wrapper"
                     v-bind="attrs"
                     v-on="on">
                  <AvatarImage class="staff-avatar"
                               size="x-sm"
                               :class="{ 'staff-avatar--initial': !staff.photo_path && !allAdditionalInitials }"
                               :imagePath="staff.photo_path"
                               :name="staff.display_name"
                               :color-id="staff.color_id"/>
                </div>
              </template>
              <span>
                {{staff.display_name}}
              </span>
            </v-tooltip>
            <!--all extra staff in one icon-->
            <v-tooltip top
                       content-class="avatar-tooltip"
                       v-if="moreStaff.length"
                       v-model="showAdditionalStaffNames">
              <template v-slot:activator="{ on, attrs }">
                <div class="staff-avatar-wrapper"
                     v-bind="attrs"
                     v-on="on">
                  <AvatarImage class="staff-avatar border--light-gray"
                               size="x-sm"
                               :click="switchAdditionalStaffNames"
                               :text="moreStaffAmountStr"/>
                </div>
              </template>
              <ul class="additional-staff__name-list" v-if="moreStaff.length">
                <li v-for="staff in moreStaffNames">{{staff}}</li>
              </ul>
            </v-tooltip>
          </div>

          <button class="matter-staff__change--btn"
                  :class="{'only-add': !isAdditionalStaff}"
                  v-if="allowAddRemoveBtn"
                  @click="openAddStaffDialog">
            {{!isAdditionalStaff ? $t('staff.client_assign.add_additional_staff') : $t('staff.client_assign.add_remove_staff')}}
          </button>
        </div>
      </div>
      <MatterAdditionalStaffAssignDialog v-if="isAddStaffDialogOpen"
                                         :matter="matter"
                                         :add-mode="!additionalStaff.length"
                                         :additional-staff="additionalStaff"
                                         :available-staff="availableStaffNoAssignee"
                                         @update-collaborators="updateCollaborators"
                                         @close-dialog="closeAddStaffDialog"/>
      <Toast v-model="isToasting" :text="toastText" :color="toastStatus"/>
    </div>
  </SkeletonText>
</template>

<script>
  import {AvatarImage} from "@/modules/common/components";
  import {mapGetters} from "vuex";
  import DialogCmp from "@/modules/common/components/dialog/DialogCmp";
  import MatterAdditionalStaffAssignDialog from "@/modules/matters/components/matterPane/matterDetails/MatterAdditionalStaffAssignDialog";
  import {SkeletonText} from '@/modules/common/components';
  import Toast from "@/modules/common/components/toast/Toast";

  export default {
    name: "MatterStaff",
    components: {MatterAdditionalStaffAssignDialog, DialogCmp, AvatarImage, Toast, SkeletonText},
    props: {
      matter: {
        type: Object,
        required: true
      },
      allStaff: {
        type: Array,
        required: true
      },
      collaborators: {
        type: Array,
        required: true
      }
    },
    data() {
      return {
        tCollaborators: 'staff.client_assign.',
        tDialog: 'dialog.',
        tToast: 'toast.',
        isAddStaffDialogOpen: false,
        showAdditionalStaffNames: false,
        toShowToast: false,
        isToasting: false,
        toastText: `${this.tCollaborators}${this.tToast}success_add`,
        toastColorFail: 'red',
        toastColorSuccess: 'green',
        toastStatus: this.toastColorSuccess
      }
    },
    computed: {
      ...mapGetters('accountModule', ['isCollaborator', 'staff']),
      ...mapGetters('mattersModule', ['currContact']),
      isAdditionalStaff() {
        return !!this.additionalStaff.length
      },
      availableStaff() {
        return this.allStaff.filter(member => member.active);
      },
      availableStaffNoAssignee(){
        return this.availableStaff.filter(staff => staff.uid !== this.assignedStaff.uid);
      },
      assignedStaff() {
        return this.allStaff.find(member => member.active && member.uid === this.matter.staff.uid);
      },
      additionalStaff() {
        return this.collaborators.map(collaborator => this.allStaff.find(member => member.uid === collaborator.staff_uid)).filter(member => member && member.active && member.uid !== this.assignedStaff.uid);
      },
      allAdditionalInitials() {
        return !this.additionalStaff.filter(staff => staff.photo_path).length;
      },
      allAdditionalPhotos() {
        if (this.additionalStaff.length === 0) return false;
        return this.additionalStaff.filter(staff => staff.photo_path).length === this.additionalStaff.length;
      },
      moreStaff() {
        return this.additionalStaff.slice(3);
      },
      moreStaffAmountStr() {
        return `+${this.moreStaff.length}`;
      },
      moreStaffNames(){
        return this.moreStaff.length && this.moreStaff.map(staff => staff.full_name).sort();
      },
      isPane() {
        return this.$store.getters.isPane;
      },
      allowAddRemoveBtn() {
        const isAssigneeStaffLogin = this.staff.uid === this.assignedStaff.uid;

        const rollsPermitted = ['admin', 'collaborator', 'manager'];
        const isStaffRollPermitted = rollsPermitted.find(role => role === this.staff.role);

        return !this.isPane && (isStaffRollPermitted || isAssigneeStaffLogin);
      },
      showAdditionalStaff() {
        if (this.availableStaff.length) {
          return this.currContact && this.currContact.uid && (!this.isPane || !!this.additionalStaff.length)
        }
        return false
      },
      loading() {
        return this.availableStaff.length === 0 || (!this.showAdditionalStaff && !this.isPane);
      }
    },
    methods: {
      changeStaffClick() {
        this.$emit('change-staff');
      },
      openAddStaffDialog() {
        const trackEvent = this.isAdditionalStaff ? 'Edit additional staff' : 'Add additional staff';
        this.$track(trackEvent);
        this.isAddStaffDialogOpen = true;
      },
      closeAddStaffDialog() {
        this.isAddStaffDialogOpen = false;
      },
      switchAdditionalStaffNames() {
        this.showAdditionalStaffNames = !this.showAdditionalStaffNames;
      },
      async updateCollaborators({isSuccess}) {
        if(isSuccess) {
          this.toastText = `${this.tCollaborators}${this.tToast}success_${this.additionalStaff.length > 0 ? 'update' : 'add'}`
          this.toastStatus = this.toastColorSuccess;
        } else {
          this.toastText = `${this.tCollaborators}${this.tToast}fail`;
          this.toastStatus = this.toastColorFail;
        }
        this.isAddStaffDialogOpen = false;
        this.$emit('update-collaborators');
        this.isToasting = true;
      }
    }
  }
</script>

<style lang="scss" scoped>
  @import "@/assets/scss/abstract/_variables.scss";
  @import "@/assets/scss/abstract/_mixins.scss";

  .matter-staff {

    &--photo {
      margin-bottom: -10px;
    }
    .staff {
      display: flex;
      align-items: center;
    }

    .primary-staff {
      margin-bottom: 10px;

      &--single {
        margin-bottom: -10px;
      }
    }

    .staff-title {
      width: 96px;
      color: $grey-color-6;

      &--photos {
        margin-bottom: 8px;
      }

      &--single {
        width: 88px;
      }
      &--gape {
        width: 96px;
      }
    }

    .staff {
      .avatars {
        display: flex;
      }

      &__chips {
        display: flex;
      }

      &-avatar-wrapper {
        display: inline-block;
        margin: 0 5px 0 -8px;
        position: relative;

        &:first-of-type {
          margin: 0 5px;
        }

        $upToChildren: 4;
        @for $i from 1 through $upToChildren {
          &:nth-of-type(#{$upToChildren - $i}) {
            z-index: $i;
          }
        }
      }
    }

    &__unassigned{
      display: flex;
      align-items: center;
    }

    &__change {
      &--btn {
        font-size: 10px;
        margin: 0 5px;
        color: $blue-color-3;
        cursor: pointer;

        &--initials {
          top: 12px;
        }

        &.only-add {
          margin-left: 12px;
        }
      }
    }
  }

  .additional-staff {

    &__name {
      &-list {
        list-style-type: none;
        padding: 0;

        li {
          color: white;
        }
      }
    }
  }

  .avatar-tooltip {
    font-family: var(--primary-font-family);
    font-size: 12px;
    line-height: 18px;
  }

  .v-tooltip__content {
    background-color: rgba($black-color-1, .87);
  }
</style>
